import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  MenuItem,
  Avatar,
  TablePagination,
  // ...existing code...
  CircularProgress,
  Alert,
  Tooltip,
  Divider,
  Tabs,
  Tab,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Add,
  Edit,
  Delete,
  Search,
  Person,
  Email,
  Phone,
  Work,
  LocationOn,
  Security,
  Badge,
  Cake,
  // ...existing code...
  CloudUpload,
  Close,
} from "@mui/icons-material";
import { colaboradorService } from "../../services/colaboradorService";
import EmailTestDialog from "../../components/admin/EmailTestDialog";
import MobileDataCard, { MobileDataList } from "../../components/common/MobileDataCard";

const EstadoChip = ({ estado }) => {
  const isActive = estado === "Activo" || estado === "activo";
  return (
    <Chip
      label={isActive ? "Activo" : "Inactivo"}
      color={isActive ? "success" : "error"}
      variant="soft" // "soft" variant requires newer MUI or custom theme, falling back to filled/outlined if needed
      size="small"
      icon={isActive ? <Security fontSize="small" /> : undefined}
      sx={{ fontWeight: "bold" }}
    />
  );
};

const TabPanel = (props) => {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: { xs: 2, md: 3 } }}>{children}</Box>}
    </div>
  );
};

const AdminColaboradores = () => {
  // Detección de móvil
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // State
  const [colaboradores, setColaboradores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Dialog State
  const [open, setOpen] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [isEdit, setIsEdit] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [currentUsuarioId, setCurrentUsuarioId] = useState(null);
  const [emailTestOpen, setEmailTestOpen] = useState(false);

  // Modal de visualización de colaborador
  const [viewModalOpen, setViewModalOpen] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [selectedColaborador, setSelectedColaborador] = useState(null);

  // Photo Upload State
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoUploadSuccess, setPhotoUploadSuccess] = useState(false);

  // Original data for change detection
  const [originalData, setOriginalData] = useState(null);

  // Form State
  const initialForm = {
    nombre_completo: "",
    email: "",
    telefono: "",
    rol: "Colaborador",
    ubicacion: "",
    especialidad: "Fotógrafo",
    fecha_cumple: "",
    direccion: "",
    foto_url: "",
    username: "",
    password: "Alavueta3602026",
    estado: "Activo",
  };
  const [formData, setFormData] = useState(initialForm);

  // Fetch Data
  const fetchData = async () => {
    try {
      setLoading(true);
      const data = await colaboradorService.getAll();

      // Aceptar tanto formato { data: [...] } como un array plano
      const list = Array.isArray(data) ? data : data?.data || [];

      setColaboradores(list);
      setError("");
    } catch (err) {
      const errorMsg =
        err.response?.data?.error ||
        err.message ||
        "Error al cargar colaboradores. Verifique la conexión.";
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const generateUsernames = (fullName) => {
    if (!fullName) return [];
    const cleanName = fullName
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .trim();
    const parts = cleanName.split(/\s+/);

    if (parts.length < 2) return [cleanName];

    let primerNombre = parts[0];
    let primerApellido = "";
    let segundoNombre = "";
    let segundoApellido = "";

    if (parts.length === 2) {
      primerApellido = parts[1];
    } else if (parts.length >= 3) {
      if (parts.length === 3) {
        primerApellido = parts[1];
        segundoApellido = parts[2];
      } else {
        segundoNombre = parts[1];
        primerApellido = parts[2];
        segundoApellido = parts[3];
      }
    }

    if (!primerApellido) return [cleanName];

    let usernameOptions = [];

    // 1. eric.perez
    usernameOptions.push(`${primerNombre}.${primerApellido}`);

    // 2. erics.perez
    if (segundoNombre) {
      usernameOptions.push(
        `${primerNombre}${segundoNombre.charAt(0)}.${primerApellido}`,
      );
    }

    // 3. erict.perez
    if (segundoApellido) {
      usernameOptions.push(
        `${primerNombre}${segundoApellido.charAt(0)}.${primerApellido}`,
      );
    }

    usernameOptions.push(
      `${primerNombre}.${primerApellido}${Math.floor(Math.random() * 100)}`,
    );

    return usernameOptions;
  };

  const handleNameBlur = async () => {
    if (isEdit || !formData.nombre_completo) return;
    const options = generateUsernames(formData.nombre_completo);

    // Check 1
    let check = await colaboradorService.checkUsername(options[0]);
    if (check.available) {
      setFormData((prev) => ({ ...prev, username: options[0] }));
      return;
    }

    // Check 2
    if (options[1]) {
      check = await colaboradorService.checkUsername(options[1]);
      if (check.available) {
        setFormData((prev) => ({ ...prev, username: options[1] }));
        return;
      }
    }

    // Check 3
    if (options[2]) {
      check = await colaboradorService.checkUsername(options[2]);
      if (check.available) {
        setFormData((prev) => ({ ...prev, username: options[2] }));
        return;
      }
    }

    setFormData((prev) => ({
      ...prev,
      username: options[0] + Math.floor(Math.random() * 99),
    }));
  };

  const validateFormData = () => {
    const required = ["nombre_completo", "email", "username"];
    const missing = required.filter(
      (field) => !formData[field] || formData[field].trim() === "",
    );

    if (missing.length > 0) {
      alert(
        `Por favor completa los campos obligatorios: ${missing.map((f) => f.replace(/_/g, " ")).join(", ")}`,
      );
      return false;
    }

    // Validar email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      alert("Por favor ingresa un email válido");
      return false;
    }

    return true;
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    console.log("[PhotoUpload] Iniciando subida:", file.name);
    setUploadingPhoto(true);
    setPhotoUploadSuccess(false);
    try {
      const res = await colaboradorService.uploadPhoto(file);
      console.log("[PhotoUpload] Respuesta del servidor:", res);
      // El interceptor de api.js ya desenvuelve { success, data } -> res es directamente los datos
      const photoUrl = res?.url || res?.data?.url;
      if (photoUrl) {
        const newUrl = photoUrl;
        console.log("[PhotoUpload] Nueva URL recibida:", newUrl);
        setFormData((prev) => {
          console.log("[PhotoUpload] URL anterior:", prev.foto_url);
          console.log("[PhotoUpload] URL nueva:", newUrl);
          return { ...prev, foto_url: newUrl };
        });
        setPhotoUploadSuccess(true);
      } else {
        const msg = res?.message || "Error desconocido al procesar imagen";
        alert(`Error al subir foto: ${msg}`);
      }
    } catch (err) {
      const msg =
        err.response?.data?.message || err.message || "Error de conexión";
      alert(`Fallo al subir imagen: ${msg}`);
    } finally {
      setUploadingPhoto(false);
    }
  };

  // Handlers
  const handleOpen = (colab = null) => {
    setPhotoUploadSuccess(false);
    if (colab) {
      console.log("[handleOpen] Abriendo colaborador para editar:", colab);
      setIsEdit(true);
      setCurrentId(colab.id);
      setCurrentUsuarioId(colab.usuario_id);
      // Normalizar TODOS los campos comparados en hasChanges para evitar falsos negativos
      const data = {
        ...colab,
        password: "", // Don't show password on edit
        username: colab.username || "",
        email: colab.email || "",
        nombre_completo: colab.nombre_completo || "",
        telefono: colab.telefono || "",
        ubicacion: colab.ubicacion || "",
        direccion: colab.direccion || "",
        foto_url: colab.foto_url || "",
        // Campos adicionales que se comparan en hasChanges
        rol: colab.rol || "Colaborador",
        especialidad: colab.especialidad || "",
        fecha_cumple: colab.fecha_cumple || "",
        estado: colab.estado || "Activo",
      };
      console.log("[handleOpen] Datos inicializados:", data);
      console.log("[handleOpen] foto_url inicial:", data.foto_url);
      // Crear copia independiente para originalData para asegurar comparación correcta
      setFormData({ ...data });
      setOriginalData({ ...data });
    } else {
      setIsEdit(false);
      setFormData(initialForm);
      setOriginalData(null);
    }
    setTabValue(0);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setFormData(initialForm);
    setPhotoUploadSuccess(false);
    setOriginalData(null);
  };

  // Detectar si hay cambios en el formulario (memoizado para mejor performance)
  const hasChanges = useMemo(() => {
    if (!isEdit) {
      console.log("[hasChanges] Modo crear - siempre habilitado");
      return true; // Siempre habilitado para nuevos colaboradores
    }
    if (!originalData) {
      console.log("[hasChanges] No hay originalData");
      return false;
    }

    // Campos a comparar
    const fieldsToCompare = [
      "nombre_completo",
      "email",
      "telefono",
      "rol",
      "ubicacion",
      "especialidad",
      "fecha_cumple",
      "direccion",
      "foto_url",
      "estado",
    ];

    // Función helper para normalizar valores (null/undefined -> "", trim strings)
    const normalize = (val) => {
      if (val === null || val === undefined) return "";
      return String(val).trim();
    };

    const changes = fieldsToCompare.filter(
      (field) => normalize(formData[field]) !== normalize(originalData[field]),
    );

    console.log("[hasChanges] Cambios detectados:", changes);
    if (changes.length > 0) {
      changes.forEach((field) => {
        console.log(
          `[hasChanges] ${field}: "${originalData[field]}" -> "${formData[field]}"`,
        );
      });
    }

    return changes.length > 0;
  }, [isEdit, originalData, formData]);

  const handleDelete = async (colab) => {
    if (
      window.confirm(
        `¿Estás seguro de desactivar al colaborador ${colab.nombre_completo}?`,
      )
    ) {
      try {
        await colaboradorService.delete(colab.id, colab.usuario_id);
        fetchData();
      } catch (err) {
        alert("Error al eliminar");
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validar datos antes de enviar
    if (!validateFormData()) {
      return;
    }

    try {
      if (isEdit) {
        await colaboradorService.update({
          ...formData,
          id: currentId,
          usuario_id: currentUsuarioId,
        });
        alert("✓ Colaborador actualizado correctamente");
      } else {
        // 1. Crear Usuario + Colaborador en backend
        const res = await colaboradorService.create(formData);

        if (!res || res.success === false) {
          const apiMsg =
            res?.message || "Error desconocido al crear colaborador";
          throw new Error(apiMsg);
        }

        // 2. Enviar correo de bienvenida SOLO si la creación fue exitosa
        try {
          await colaboradorService.sendWelcomeEmail({
            email: formData.email,
            nombre: formData.nombre_completo,
            username: formData.username,
            password: formData.password,
          });
          alert(
            `✓ Colaborador creado exitosamente\nID: ${res.id}\nUsuario: ${formData.username}\n\nCorreo de bienvenida enviado`,
          );
        } catch (emailErr) {
          const emailMsg = emailErr.response?.data?.message || emailErr.message;
          alert(
            `⚠ Colaborador creado (ID: ${res.id}), pero falló el envío del correo.\nDetalles: ${emailMsg}`,
          );
        }
      }
      handleClose();
      fetchData();
    } catch (err) {
      const msg =
        err.response?.data?.message || err.message || "Error desconocido";
      alert("❌ Error al guardar: " + msg);
    }
  };

  // Filter & Pagination
  const filteredColaboradores = colaboradores.filter(
    (colab) =>
      colab.nombre_completo?.toLowerCase().includes(search.toLowerCase()) ||
      colab.email?.toLowerCase().includes(search.toLowerCase()) ||
      colab.rol?.toLowerCase().includes(search.toLowerCase()),
  );

  const stats = {
    total: colaboradores.length,
    activos: colaboradores.filter(
      (c) => c.estado === "Activo" || c.estado === "activo",
    ).length,
    inactivos: colaboradores.filter(
      (c) => c.estado !== "Activo" && c.estado !== "activo",
    ).length,
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, overflowX: { xs: "hidden", sm: "visible" }, width: "100%", boxSizing: "border-box" }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: { xs: 2, sm: 3 },
          flexWrap: { xs: "wrap", sm: "nowrap" },
          gap: 1,
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: "#fff",
            fontSize: { xs: "1.25rem", sm: "2.125rem" },
          }}
        >
          Equipo de Colaboradores
        </Typography>
        {/* Botones desktop */}
        <Box sx={{ display: { xs: "none", sm: "flex" }, gap: 2 }}>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpen()}
            sx={{
              background: "linear-gradient(45deg, #1a237e 30%, #283593 90%)",
              boxShadow: "0 3px 5px 2px rgba(26, 35, 126, .3)",
              borderRadius: 2,
            }}
          >
            Nuevo Colaborador
          </Button>
          <Button
            variant="outlined"
            onClick={() => setEmailTestOpen(true)}
            sx={{
              borderColor: "#00C9FF",
              color: "#00C9FF",
              borderRadius: 2,
              "&:hover": {
                backgroundColor: "rgba(0, 201, 255, 0.04)",
                borderColor: "#00C9FF",
              },
            }}
          >
            🧪 Probar Email
          </Button>
        </Box>
        {/* Botones móvil */}
        <Box sx={{ display: { xs: "flex", sm: "none" }, gap: 1 }}>
          <IconButton
            onClick={() => handleOpen()}
            sx={{
              width: 44,
              height: 44,
              background: "linear-gradient(45deg, #1a237e 30%, #283593 90%)",
              color: "#fff",
              boxShadow: "0 3px 8px rgba(26, 35, 126, .4)",
              "&:hover": {
                background: "linear-gradient(45deg, #283593 30%, #3949ab 90%)",
              },
            }}
          >
            <Add />
          </IconButton>
          <IconButton
            onClick={() => setEmailTestOpen(true)}
            sx={{
              width: 44,
              height: 44,
              border: "1px solid #00C9FF",
              color: "#00C9FF",
              fontSize: "1.2rem",
              "&:hover": {
                backgroundColor: "rgba(0, 201, 255, 0.08)",
              },
            }}
          >
            ✉️
          </IconButton>
        </Box>
      </Box>

      {/* Stats Cards - Diseño circular */}
      <Grid container spacing={{ xs: 2, sm: 3 }} justifyContent="center" sx={{ mb: { xs: 2, sm: 4 } }}>
        {/* ...existing code... (tarjetas circulares) */}
        {[
          {
            label: "Total",
            value: stats.total,
            color: "#1a237e",
            icon: <Person />,
          },
          {
            label: "Activos",
            value: stats.activos,
            color: "#2e7d32",
            icon: <Security />,
          },
          {
            label: "Inactivos",
            value: stats.inactivos,
            color: "#c62828",
            icon: <Security />,
          },
        ].map((stat, index) => (
          <Grid item xs={4} sm={4} key={index} sx={{ display: "flex", justifyContent: "center" }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                width: { xs: 90, sm: 130 },
                height: { xs: 90, sm: 130 },
                borderRadius: "50%",
                background:
                  "linear-gradient(145deg, rgba(19, 24, 41, 0.95) 0%, rgba(30, 36, 60, 0.95) 100%)",
                border: `2px solid ${stat.color}40`,
                boxShadow: `0 4px 20px ${stat.color}30`,
                transition: "transform 0.2s, box-shadow 0.2s",
                "&:hover": {
                  transform: "scale(1.05)",
                  boxShadow: `0 6px 25px ${stat.color}50`,
                },
              }}
            >
              <Avatar
                sx={{
                  bgcolor: stat.color + "30",
                  color: stat.color,
                  width: { xs: 30, sm: 40 },
                  height: { xs: 30, sm: 40 },
                  mb: 0.5,
                }}
              >
                {stat.icon}
              </Avatar>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: { xs: "0.62rem", sm: "0.75rem" } }}
              >
                {stat.label}
              </Typography>
              <Typography
                variant="h5"
                fontWeight="bold"
                sx={{
                  color: stat.color,
                  fontSize: { xs: "1.1rem", sm: "1.5rem" },
                }}
              >
                {stat.value}
              </Typography>
            </Box>
          </Grid>
        ))}
      </Grid>

      {/* Main Table Card */}
      <Paper sx={{ borderRadius: 3, overflow: "hidden", boxShadow: 3 }}>
        {/* Toolbar */}
        <Box
          sx={{
            p: 2,
            display: "flex",
            alignItems: "center",
            gap: 2,
            borderBottom: "1px solid #eee",
          }}
        >
          <TextField
            variant="outlined"
            size="small"
            placeholder="Buscar por nombre, email o rol..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ flex: 1, maxWidth: { xs: "100%", sm: 400 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ m: 2 }}>
            {error}
          </Alert>
        ) : isMobile ? (
          /* ═══════════════════════════════════════════════════════════════════
             VISTA MÓVIL - Lista simple con modal de detalles
             ═══════════════════════════════════════════════════════════════════ */
          <Box>
            <MobileDataList
              loading={loading}
              emptyMessage={`No hay colaboradores registrados`}
            >
              {filteredColaboradores
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((colab) => (
                  <MobileDataCard
                    key={colab.id}
                    title={
                      colab.nombre_completo || colab.nombre || "Sin nombre"
                    }
                    subtitle={colab.rol || ""}
                    avatarColor={
                      colab.estado === "Activo" || colab.estado === "activo"
                        ? "#4caf50"
                        : "#f44336"
                    }
                    status={{
                      label: colab.estado || "Sin estado",
                      color:
                        colab.estado === "Activo" || colab.estado === "activo"
                          ? "#4caf50"
                          : "#f44336",
                    }}
                    primaryInfo={[
                      {
                        icon: <Email fontSize="small" />,
                        label: "Email",
                        value: colab.email || "-",
                      },
                      {
                        icon: <Phone fontSize="small" />,
                        label: "Teléfono",
                        value: colab.telefono || "-",
                      },
                    ]}
                    expandedInfo={[
                      {
                        icon: <Work fontSize="small" />,
                        label: "Especialidad",
                        value: colab.especialidad || "-",
                      },
                      {
                        icon: <LocationOn fontSize="small" />,
                        label: "Ubicación",
                        value: colab.ubicacion || "-",
                      },
                    ]}
                    actions={[
                      {
                        icon: <Edit fontSize="small" />,
                        label: "Editar",
                        onClick: () => handleOpen(colab),
                      },
                      {
                        icon: <Delete fontSize="small" />,
                        label: "Eliminar",
                        color: "error",
                        onClick: () => handleDelete(colab),
                      },
                    ]}
                  />
                ))}
            </MobileDataList>
            {/* Paginación móvil */}
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={filteredColaboradores.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(e, p) => setPage(p)}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              labelRowsPerPage="Por página:"
              labelDisplayedRows={({ from, to, count }) =>
                `${from}–${to} de ${count}`
              }
              sx={{
                "& .MuiTablePagination-toolbar": {
                  flexWrap: "wrap",
                  justifyContent: "center",
                },
                "& .MuiTablePagination-selectLabel": {
                  display: { xs: "none", sm: "block" },
                },
              }}
            />
          </Box>
        ) : (
          /* ═══════════════════════════════════════════════════════════════════
             VISTA DESKTOP - Tabla tradicional
             ═══════════════════════════════════════════════════════════════════ */
          <>
            <TableContainer sx={{ overflowX: "auto" }}>
              <Table sx={{ minWidth: 650 }}>
                <TableHead
                  sx={{
                    background:
                      "linear-gradient(90deg, #0f172a 0%, #1e293b 50%, #020817 100%)",
                  }}
                >
                  <TableRow>
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        color: "#e5e7eb",
                        borderBottom: "none",
                      }}
                    >
                      ID
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        color: "#e5e7eb",
                        borderBottom: "none",
                      }}
                    >
                      Nombre Completo
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        color: "#e5e7eb",
                        borderBottom: "none",
                      }}
                    >
                      Teléfono
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        color: "#e5e7eb",
                        borderBottom: "none",
                      }}
                    >
                      Email
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        color: "#e5e7eb",
                        borderBottom: "none",
                      }}
                    >
                      Rol
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        color: "#e5e7eb",
                        borderBottom: "none",
                      }}
                    >
                      Ubicación
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        color: "#e5e7eb",
                        borderBottom: "none",
                      }}
                    >
                      Especialidad
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        color: "#e5e7eb",
                        borderBottom: "none",
                      }}
                    >
                      Estado
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        fontWeight: "bold",
                        color: "#e5e7eb",
                        borderBottom: "none",
                      }}
                    >
                      Acciones
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredColaboradores
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((colab) => (
                      <TableRow key={colab.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">
                            {9083000 + parseInt(colab.id || 0)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                            }}
                          >
                            <Avatar
                              src={colab.foto_url}
                              alt={colab.nombre_completo}
                            >
                              {colab.nombre_completo?.charAt(0)}
                            </Avatar>
                            <Box>
                              <Typography variant="subtitle2" fontWeight="bold">
                                {colab.nombre_completo}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                @{colab.username}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>{colab.telefono || "-"}</TableCell>
                        <TableCell>{colab.email}</TableCell>
                        <TableCell>
                          <Chip
                            label={colab.rol}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>{colab.ubicacion || "-"}</TableCell>
                        <TableCell>{colab.especialidad || "-"}</TableCell>
                        <TableCell>
                          <EstadoChip estado={colab.estado} />
                        </TableCell>
                        <TableCell align="center">
                          <Box
                            sx={{ display: "flex", justifyContent: "center" }}
                          >
                            <Tooltip title="Editar">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleOpen(colab)}
                              >
                                <Edit fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Desactivar/Eliminar">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDelete(colab)}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  {filteredColaboradores.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                        <Typography color="text.secondary">
                          No se encontraron colaboradores
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={filteredColaboradores.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
            />
          </>
        )}
      </Paper>

      {/* Modal Form */}
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {isEdit ? "Editar Colaborador" : "Nuevo Colaborador"}
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
            <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
              <Tab
                label="Información Personal"
                icon={<Badge />}
                iconPosition="start"
              />
              <Tab
                label="Cuenta de Usuario"
                icon={<Security />}
                iconPosition="start"
              />
            </Tabs>
          </Box>
          <form id="colab-form" onSubmit={handleSubmit}>
            <TabPanel value={tabValue} index={0}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Nombre Completo"
                    required
                    value={formData.nombre_completo}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        nombre_completo: e.target.value,
                      })
                    }
                    onBlur={handleNameBlur}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Teléfono"
                    value={formData.telefono}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        telefono: e.target.value
                          .replace(/\D/g, "")
                          .slice(0, 10),
                      })
                    }
                    inputProps={{ maxLength: 10, inputMode: "numeric" }}
                    helperText={
                      formData.telefono && formData.telefono.length < 10
                        ? `${formData.telefono.length}/10 dígitos`
                        : ""
                    }
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    select
                    label="Rol"
                    value={formData.rol}
                    onChange={(e) =>
                      setFormData({ ...formData, rol: e.target.value })
                    }
                  >
                    <MenuItem value="Colaborador">Colaborador</MenuItem>
                    <MenuItem value="Administrador">Administrador</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    select
                    label="Especialidad"
                    value={formData.especialidad}
                    onChange={(e) =>
                      setFormData({ ...formData, especialidad: e.target.value })
                    }
                  >
                    <MenuItem value="Fotógrafo">Fotógrafo</MenuItem>
                    <MenuItem value="Videógrafo">Videógrafo</MenuItem>
                    <MenuItem value="Coordinador">Coordinador</MenuItem>
                    <MenuItem value="DJ">DJ</MenuItem>
                    <MenuItem value="Vendedor">Vendedor</MenuItem>
                    <MenuItem value="Staff">Staff</MenuItem>
                    <MenuItem value="Otro">Otro</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Ubicación"
                    value={formData.ubicacion}
                    onChange={(e) =>
                      setFormData({ ...formData, ubicacion: e.target.value })
                    }
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Fecha de Cumpleaños"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    value={formData.fecha_cumple}
                    onChange={(e) =>
                      setFormData({ ...formData, fecha_cumple: e.target.value })
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Dirección Completa"
                    multiline
                    rows={2}
                    value={formData.direccion}
                    onChange={(e) =>
                      setFormData({ ...formData, direccion: e.target.value })
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography
                    variant="body2"
                    sx={{ mb: 1, fontWeight: "bold" }}
                  >
                    Foto
                  </Typography>
                  <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                    <Avatar
                      src={formData.foto_url}
                      sx={{ width: 64, height: 64, bgcolor: "#eee" }}
                    >
                      <Person />
                    </Avatar>
                    <Button
                      component="label"
                      variant="outlined"
                      startIcon={<CloudUpload />}
                    >
                      {uploadingPhoto ? "Subiendo..." : "Subir Foto"}
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        onChange={handlePhotoUpload}
                      />
                    </Button>
                    {photoUploadSuccess && (
                      <Typography variant="caption" color="success.main">
                        Foto cargada exitosamente
                      </Typography>
                    )}
                  </Box>
                  <input type="hidden" value={formData.foto_url} />
                </Grid>
              </Grid>
            </TabPanel>

            <TabPanel value={tabValue} index={1}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Usuario (Login)"
                    required
                    value={formData.username}
                    onChange={(e) =>
                      setFormData({ ...formData, username: e.target.value })
                    }
                    disabled={isEdit}
                    helperText={
                      isEdit
                        ? "El usuario no se puede cambiar"
                        : "Generado automáticamente. Puedes editarlo si está disponible."
                    }
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                  />
                </Grid>
                {!isEdit && (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Contraseña Temporal"
                      type="password"
                      required
                      value={formData.password}
                      onChange={(e) =>
                        setFormData({ ...formData, password: e.target.value })
                      }
                      helperText="Contraseña por defecto: Alavueta3602026"
                    />
                  </Grid>
                )}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    select
                    label="Estado del Sistema"
                    value={formData.estado}
                    onChange={(e) =>
                      setFormData({ ...formData, estado: e.target.value })
                    }
                  >
                    <MenuItem value="Activo">Activo</MenuItem>
                    <MenuItem value="Inactivo">Inactivo</MenuItem>
                  </TextField>
                </Grid>
              </Grid>
              {isEdit && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Si necesitas restablecer la contraseña, usa la opción "Olvidé
                  mi contraseña" en el login o crea un módulo de reset
                  administrativo.
                </Alert>
              )}
            </TabPanel>
          </form>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose}>Cancelar</Button>
          <Button
            variant="contained"
            type="submit"
            form="colab-form"
            color="primary"
            disabled={
              !formData.nombre_completo ||
              !formData.email ||
              (!isEdit && !formData.username) || // username solo requerido al crear
              (isEdit && !hasChanges)
            }
            title={
              isEdit
                ? hasChanges
                  ? "Guardar cambios realizados"
                  : "No hay cambios para guardar"
                : "Completa los campos para guardar"
            }
          >
            {isEdit ? "Guardar Cambios" : "Guardar y Enviar Bienvenida"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Email Test Dialog */}
      <EmailTestDialog
        open={emailTestOpen}
        onClose={() => setEmailTestOpen(false)}
      />

      {/* Modal de Visualización de Colaborador */}
      <Dialog
        open={viewModalOpen}
        onClose={() => setViewModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: "linear-gradient(145deg, #1a1f35 0%, #0d1117 100%)",
            borderRadius: 3,
            border: "1px solid rgba(255, 255, 255, 0.1)",
          },
        }}
      >
        {selectedColaborador && (
          <>
            <DialogTitle
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                pb: 1,
              }}
            >
              <Typography variant="h6" fontWeight="bold">
                Detalles del Colaborador
              </Typography>
              <IconButton
                onClick={() => setViewModalOpen(false)}
                sx={{ color: "text.secondary" }}
              >
                <Close />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ pt: 2 }}>
              {/* Header con Avatar y Nombre */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  mb: 3,
                  pb: 2,
                  borderBottom: "1px solid rgba(255,255,255,0.1)",
                }}
              >
                <Avatar
                  src={selectedColaborador.foto_url}
                  sx={{
                    width: 80,
                    height: 80,
                    mb: 1.5,
                    bgcolor:
                      selectedColaborador.estado === "Activo"
                        ? "#4caf50"
                        : "#f44336",
                    fontSize: "1.5rem",
                  }}
                >
                  {(
                    selectedColaborador.nombre_completo ||
                    selectedColaborador.nombre ||
                    "U"
                  )
                    .split(" ")
                    .map((n) => n[0])
                    .slice(0, 2)
                    .join("")
                    .toUpperCase()}
                </Avatar>
                <Typography variant="h6" fontWeight="bold" textAlign="center">
                  {selectedColaborador.nombre_completo ||
                    selectedColaborador.nombre}
                </Typography>
                <Chip
                  label={selectedColaborador.estado || "Activo"}
                  color={
                    selectedColaborador.estado === "Activo"
                      ? "success"
                      : "error"
                  }
                  size="small"
                  sx={{ mt: 1 }}
                />
              </Box>

              {/* Información detallada */}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Work sx={{ color: "#9c27b0" }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Rol
                    </Typography>
                    <Typography variant="body2">
                      {selectedColaborador.rol || "-"}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Phone sx={{ color: "#4caf50" }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Teléfono
                    </Typography>
                    <Typography variant="body2">
                      {selectedColaborador.telefono || "-"}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Email sx={{ color: "#2196f3" }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Email
                    </Typography>
                    <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
                      {selectedColaborador.email || "-"}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <LocationOn sx={{ color: "#ff9800" }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Dirección
                    </Typography>
                    <Typography variant="body2">
                      {selectedColaborador.direccion || "-"}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Badge sx={{ color: "#00bcd4" }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      NSS
                    </Typography>
                    <Typography variant="body2">
                      {selectedColaborador.nss || "-"}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Security sx={{ color: "#e91e63" }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Tipo de Sangre
                    </Typography>
                    <Typography variant="body2">
                      {selectedColaborador.tipo_sangre || "-"}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Cake sx={{ color: "#ff5722" }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Fecha de Cumpleaños
                    </Typography>
                    <Typography variant="body2">
                      {selectedColaborador.fecha_cumple || "-"}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </DialogContent>
            <DialogActions sx={{ p: 2, pt: 1 }}>
              <Button
                variant="outlined"
                startIcon={<Edit />}
                onClick={() => {
                  setViewModalOpen(false);
                  handleOpen(selectedColaborador);
                }}
              >
                Editar
              </Button>
              <Button
                variant="contained"
                color="error"
                startIcon={<Delete />}
                onClick={() => {
                  setViewModalOpen(false);
                  handleDelete(selectedColaborador.id);
                }}
              >
                Eliminar
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default AdminColaboradores;
